import React from "react";
import { useNavigate } from "react-router-dom";
import {
  useChallenges,
  useChallengeOperations,
} from "../../../hooks/useChallenges";
import { useCrudModals } from "../../../hooks/useCrudModals";
import {
  Challenge,
  CreateChallengeInput,
  UpdateChallengeInput,
} from "../../../types/challenge";
import ChallengesTable from "../../../components/challenges/ChallengesTable";
import ChallengeModal from "../../../components/challenges/ChallengeModal";
import DeleteConfirmationModal from "../../../components/modals/DeleteConfirmationModal";
import ListPageShell from "../../../components/common/ListPageShell";

const toChallengeInput = (
  data: any
): CreateChallengeInput & UpdateChallengeInput => ({
  name: data.name,
  conducted_at_year: data.conducted_at_year,
  is_active: data.is_active,
});

// Challenges are created by the API, not here: the page has no Add button.
const Challenges: React.FC = () => {
  const navigate = useNavigate();
  const modals = useCrudModals<Challenge>();

  const { challenges, loading, error, refresh } = useChallenges();

  const {
    loading: operationLoading,
    createChallenge,
    updateChallenge,
    deleteChallenge,
    error: operationError,
  } = useChallengeOperations();

  const handleViewChallenge = (challenge: Challenge) => {
    navigate(`/admin/challenges/${challenge.id}`);
  };

  const confirmDeleteChallenge = async () => {
    if (!modals.toDelete) return;

    const success = await deleteChallenge(modals.toDelete.id);
    if (success) {
      refresh();
      modals.closeDelete();
    }
  };

  const handleCreateSubmit = async (challengeData: any) => {
    const newChallenge = await createChallenge(toChallengeInput(challengeData));
    if (newChallenge) {
      refresh();
      modals.closeCreate();
    }
  };

  const handleUpdateSubmit = async (challengeData: any) => {
    if (!modals.selected) return;

    const updatedChallenge = await updateChallenge(
      modals.selected.id,
      toChallengeInput(challengeData)
    );
    if (updatedChallenge) {
      refresh();
      modals.closeEdit();
    }
  };

  return (
    <>
      <ListPageShell
        entityName="challenges"
        onRefresh={refresh}
        refreshDisabled={loading}
        error={error}
        operationError={operationError}
      >
        <ChallengesTable
          challenges={challenges}
          loading={loading || operationLoading}
          onEdit={modals.openEdit}
          onDelete={modals.openDelete}
          onView={handleViewChallenge}
        />
      </ListPageShell>

      <ChallengeModal
        isOpen={modals.showCreate}
        onClose={modals.closeCreate}
        onSubmit={handleCreateSubmit}
        mode="create"
        loading={operationLoading}
        error={operationError}
      />

      <ChallengeModal
        isOpen={modals.showEdit}
        onClose={modals.closeEdit}
        onSubmit={handleUpdateSubmit}
        challenge={modals.selected}
        mode="edit"
        loading={operationLoading}
        error={operationError}
      />

      <DeleteConfirmationModal
        isOpen={modals.showDelete}
        onClose={modals.closeDelete}
        onConfirm={confirmDeleteChallenge}
        title="Delete Challenge"
        message="Are you sure you want to delete this challenge?"
        itemName={modals.toDelete?.name || "Untitled Challenge"}
        loading={operationLoading}
        error={operationError}
      />
    </>
  );
};

export default Challenges;
